/**
 * Login persistence for NostrLoginProvider.
 *
 * - Default: localStorage (survives browser restart) — extension/bunker OK.
 * - Session-only mode: sessionStorage — nsec wiped when the tab/window closes.
 *
 * Not a cryptographic vault. XSS can still read whichever store is active
 * while the page is open. Prefer NIP-07 / bunker / nostrconnect over nsec.
 */

const SESSION_FLAG = 'opendork:login-session-only';

let sessionOnlyPreference = false;

try {
  sessionOnlyPreference = sessionStorage.getItem(SESSION_FLAG) === '1';
} catch {
  sessionOnlyPreference = false;
}

/** Call before nsec login when user opts into session-only. */
export function setLoginSessionOnly(on: boolean): void {
  sessionOnlyPreference = on;
  try {
    if (on) {
      sessionStorage.setItem(SESSION_FLAG, '1');
      // Drop any durable login copy so nsec doesn't linger
    } else {
      sessionStorage.removeItem(SESSION_FLAG);
    }
  } catch {
    // ignore
  }
}

export function isLoginSessionOnly(): boolean {
  return sessionOnlyPreference;
}

function read(key: string): string | null {
  try {
    // Prefer session copy when session-only (or leftover from a session login)
    const s = sessionStorage.getItem(key);
    if (s != null) return s;
  } catch {
    // ignore
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  if (sessionOnlyPreference) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // fall through
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function remove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Adapter for @nostrify/react NostrLoginProvider. */
export const loginStorage = {
  async getItem(key: string): Promise<string | null> {
    return read(key);
  },

  async setItem(key: string, value: string): Promise<void> {
    write(key, value);
  },

  async removeItem(key: string): Promise<void> {
    remove(key);
  },
};

/** @deprecated Use loginStorage — name implied crypto that is not present. */
export const secureStorage = loginStorage;
